import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface RollingNumberProps {
  value: number;
}

export const RollingNumber = ({ value }: RollingNumberProps) => {
  const [targetDigits, setTargetDigits] = useState<number[]>(String(value).split('').map(Number));
  const [animationKey, setAnimationKey] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const digitHeight = 20; // px

  useEffect(() => {
    // 클라이언트에서만 애니메이션 시작
    if (typeof window === 'undefined') return;

    // 첫 애니메이션: 1.8초 후
    const firstTimeout = setTimeout(() => {
      setAnimationKey((prev) => prev + 1);

      // 이후 5초마다 반복
      intervalRef.current = setInterval(() => {
        setAnimationKey((prev) => prev + 1);
      }, 5000);
    }, 1800);

    return () => {
      clearTimeout(firstTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const strDigits = String(value).split('').map(Number);
    setTargetDigits(strDigits);
  }, [value]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {targetDigits.map((digit, index) => {
        // 자리별 애니메이션 duration: 왼쪽 600ms, 오른쪽 +100ms
        const duration = Math.min(600 + index * 100, 3000) / 1000; // 초 단위로 변환

        // 각 자리수의 시작 숫자부터 한 바퀴 도는 배열 생성
        // 예: digit이 3이면 [3,4,5,6,7,8,9,0,1,2,3]
        const rollingDigits: number[] = [];
        for (let i = 0; i <= 10; i++) {
          rollingDigits.push((digit + i) % 10);
        }

        // 3자리마다 콤마 추가 (오른쪽부터 계산)
        const totalDigits = targetDigits.length;
        const positionFromRight = totalDigits - index;
        const needsComma = positionFromRight % 3 === 0 && index !== 0;

        return (
          <>
            {needsComma && <span key={`comma-${index}`}>,</span>}
            <div
              key={index}
              style={{
                overflow: 'hidden',
                height: `${digitHeight}px`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <motion.div
                key={`${index}-${animationKey}`}
                initial={{ y: 0 }}
                animate={{ y: -10 * digitHeight }}
                transition={{
                  type: 'tween',
                  duration,
                  ease: [0.42, 0, 0.58, 1], // Ease In Out Cubic
                }}
              >
                {rollingDigits.map((d, i) => (
                  <div key={i} style={{ height: `${digitHeight}px`, lineHeight: `${digitHeight}px` }}>
                    {d}
                  </div>
                ))}
              </motion.div>
            </div>
          </>
        );
      })}
    </div>
  );
};
